type HexColor = `#${string}`;

type RgbColor = [number, number, number] | [number, number, number, number];

const toRgb = (color: HexColor): RgbColor => {
  const hex = color.replace('#', '');
  const red = parseInt(hex.substring(0, 2), 16);
  const green = parseInt(hex.substring(2, 4), 16);
  const blue = parseInt(hex.substring(4, 6), 16);
  if (hex.length > 6) {
    const alpha = parseInt(hex.substring(6, 8), 16);
    return [red, green, blue, alpha];
  }
  return [red, green, blue];
};

const toHex = (color: RgbColor): HexColor => {
  const red = color[0].toString(16).padStart(2, '0');
  const green = color[1].toString(16).padStart(2, '0');
  const blue = color[2].toString(16).padStart(2, '0');
  if (color.length > 3) {
    const alpha = (color[3] ?? 255).toString(16).padStart(2, '0');
    return `#${red}${green}${blue}${alpha}`;
  }
  return `#${red}${green}${blue}`;
};

const alpha = <T extends HexColor | RgbColor>(
  color: T,
  alpha: number
): T extends HexColor ? HexColor : RgbColor => {
  if (alpha < 0 || alpha > 1) {
    throw new Error('Alpha value must be between 0 and 1');
  }
  if (typeof color === 'string') {
    return `#${color.replace('#', '').slice(0, 6)}${Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0')}` as T extends HexColor ? HexColor : RgbColor;
  }
  const [red, green, blue] = color;
  return [red, green, blue, Math.round(alpha * 255)] as T extends HexColor
    ? HexColor
    : RgbColor;
};

export type { HexColor, RgbColor };
export { toRgb, toHex, alpha };
